<template>
  <!-- Placeholder div - actual video element will be positioned to match this -->
  <div ref="placeholder" class="video-placeholder"></div>
</template>

<script>
  import playerMixins from '@/models/player';
  import { simpleTypeMixin } from '@/helpers';
  import { getItemLocalUrl, getItemUrl } from '@/utils/mediaAsset';
  import { logger } from '@/utils/screenEventsLogger';

  export default {
    name: 'TizenVideoPlayer',

    mixins: [playerMixins, simpleTypeMixin],

    props: {
      itemId: {
        type: String,
        default: '',
      },
      itemType: {
        type: String,
        default: '',
      },
      itemUrl: {
        type: String,
        default: '',
      },
      imageMode: {
        type: String,
        default: 'cover',
      },
      thumbnail: {
        type: String,
        default: '',
      },
      zIndex: {
        type: Number,
        default: 0,
      },
    },

    data() {
      return {
        videoElement: null,
        wrapperElement: null,
      };
    },

    mounted() {
      this.createVideoElement();

      this.$nextTick(() => {
        this.updateVideoPosition();
        this.playVideo();
      });
    },

    beforeDestroy() {
      // Clean up wrapper element (which contains the video) when component is destroyed
      if (this.wrapperElement && this.wrapperElement.parentNode) {
        this.wrapperElement.parentNode.removeChild(this.wrapperElement);
      }
    },

    methods: {
      createVideoElement() {
        // Create wrapper div for clipping
        const wrapper = document.createElement('div');
        wrapper.id = `tizen_video_wrapper_${this.itemId}`;
        wrapper.style.cssText = `position: fixed; z-index: ${
          this.zIndex + 10
        }; overflow: hidden; opacity: 1;`;

        // Create video element
        const video = document.createElement('video');
        video.id = `tizen_video_${this.itemId}`;
        video.loop = true;
        video.muted = true;
        video.src = '';
        video.style.cssText = `position: absolute;`;

        wrapper.appendChild(video);
        document.body.appendChild(wrapper);

        this.wrapperElement = wrapper;
        this.videoElement = video;
      },

      getVideoThumbnailUrl(thumbnailUrl) {
        return getItemUrl(thumbnailUrl);
      },

      updateVideoPosition() {
        if (!this.videoElement || !this.wrapperElement || !this.$refs.placeholder) return;

        const rect = this.$refs.placeholder.getBoundingClientRect();
        const containerWidth = rect.width;
        const containerHeight = rect.height;
        const containerAspect = containerWidth / containerHeight;

        // Position and size the wrapper to match the placeholder (this will clip the video)
        this.wrapperElement.style.top = `${rect.top}px`;
        this.wrapperElement.style.left = `${rect.left}px`;
        this.wrapperElement.style.width = `${containerWidth}px`;
        this.wrapperElement.style.height = `${containerHeight}px`;

        this.videoElement.addEventListener(
          'loadedmetadata',
          () => {
            const videoAspect = this.videoElement.videoWidth / this.videoElement.videoHeight;
            let renderWidth, renderHeight, offsetX, offsetY;

            if (this.imageMode === 'cover') {
              if (videoAspect > containerAspect) {
                renderHeight = containerHeight;
                renderWidth = renderHeight * videoAspect;
                offsetX = (containerWidth - renderWidth) / 2;
                offsetY = 0;
              } else {
                renderWidth = containerWidth;
                renderHeight = renderWidth / videoAspect;
                offsetX = 0;
                offsetY = (containerHeight - renderHeight) / 2;
              }
            } else if (this.imageMode === 'contain') {
              if (videoAspect > containerAspect) {
                renderWidth = containerWidth;
                renderHeight = renderWidth / videoAspect;
                offsetX = 0;
                offsetY = (containerHeight - renderHeight) / 2;
              } else {
                renderHeight = containerHeight;
                renderWidth = renderHeight * videoAspect;
                offsetX = (containerWidth - renderWidth) / 2;
                offsetY = 0;
              }
            } else {
              renderWidth = containerWidth;
              renderHeight = containerHeight;
              offsetX = 0;
              offsetY = 0;
            }

            this.videoElement.style.width = `${renderWidth}px`;
            this.videoElement.style.height = `${renderHeight}px`;
            this.videoElement.style.left = `${offsetX}px`;
            this.videoElement.style.top = `${offsetY}px`;
          },
          { once: true },
        );
      },

      playVideo() {
        const videoRef = this.videoElement;
        if (!videoRef) return;

        videoRef.addEventListener('error', (e) =>
          logger.addLog(`Tizen video error: ${e}, ${videoRef.error}`),
        );

        videoRef.src = getItemLocalUrl(this.itemUrl, this.itemId);
        videoRef.load();

        const playPromise = videoRef.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => logger.addLog(`Tizen video play failed: ${error}`));
        }
      },
    },
  };
</script>

<style scoped>
  .video-placeholder {
    width: 100%;
    height: 100%;
    position: relative;
  }
</style>
